import PropTypes from 'prop-types';
import React from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { FormattedHTMLMessage } from '@bibliocommons/react-intl';
import { withRouter } from '@bibliocommons/utils-routing';
import { bindAll } from '@bibliocommons/utils-react';
import { userIdentityShape, routerShape } from '@bibliocommons/bc-prop-types';
import { DropdownMenuDivider, DropdownMenuHeader, DropdownMenuItem } from '@bibliocommons/deprecated-base-dropdown';

import { SHELF_NAMES } from 'app/constants/ShelfConstants';
import BatchActionsListContainer from 'app/components/shared/BatchActions/BatchActionsList/BatchActionsListContainer';
import BatchActionsListItem from 'app/components/shared/BatchActions/BatchActionsListItem';
import ItemList from 'app/components/shared/ItemList';
import BatchActionLink from 'app/components/shared/BatchActions/BatchActionLink';
import shelfShape from 'app/shapes/shelves/shelfShape';
import ShelfListItem from '../ShelfListItem';
import DeleteConfirmationModal from '../ShelfListItem/DeleteConfirmationModal';

const BATCH_REMOVE_MODAL_KEY = 'batch-remove-shelves-modal';

function privacyStatusCheck(selectedActionItems, status) {
  return selectedActionItems.every(val => status === val.get('privateItem'));
}

export class ShelfList extends React.PureComponent {
  constructor(props) {
    super(props);
    bindAll(this);
  }

  // used to trim down list of bibs related for each similar titles component
  getBibsForSimilarTitles(ownerSimilarTitles, metadataId) {
    return this.props.bibs.filter(item =>
      ownerSimilarTitles
        .get(metadataId, [])
        .some(similarTitle => item.get('id') === similarTitle.get('similarMetadataId'))
    );
  }

  handlePrivacyToggle(e, selectedActionItems, status, messageId) {
    e.preventDefault();
    const shelfName = this.props.shelf.get('name');
    const selectedItems = selectedActionItems.toList();
    const shelfItemIds = selectedItems.map(item => item.get('id')).toArray();
    const metadataIds = selectedItems.map(item => item.get('metadataId')).toArray();

    return this.props.shelfActions.updateShelfItems({
      shelfName,
      shelfItemIds,
      metadataIds,
      privateItem: status,
      isBatch: true,
      messageId,
      postSuccess: () => this.redirectToShelfPage(shelfName, selectedActionItems)
    });
  }

  handleBatchRemoveModalOpen(e) {
    e.preventDefault();
    this.props.modalActions.openModal(BATCH_REMOVE_MODAL_KEY);
  }

  handleBatchMove(e, newShelfName, selectedActionItems) {
    e.preventDefault();

    const shelfName = this.props.shelf.get('name');
    const selectedItems = selectedActionItems.toList();
    const shelfItemIds = selectedItems.map(item => item.get('id')).toArray();
    const metadataIds = selectedItems.map(item => item.get('metadataId')).toArray();
    this.props.shelfActions.updateShelfItems({
      shelfName,
      newShelfName,
      shelfItemIds,
      metadataIds,
      isBatch: true,
      messageId: 'message_successful_batch_move_shelves',
      postSuccess: () => this.redirectToShelfPage(shelfName, selectedActionItems)
    });
  }

  redirectToShelfPage(shelfName, selectedActionItems) {
    const { router, owner, shelfActions } = this.props;
    // Stay on current page by default
    let newPage = router.query.page;
    // If all items are being moved
    if (selectedActionItems.size === this.props.shelf.get('items').size) {
      newPage = 1; // Redirect to page 1
    }
    shelfActions.searchUserShelf(shelfName, owner.get('id'), {
      ...router.query,
      page: newPage
    });
  }

  renderBibList() {
    const items = this.props.shelf.get('items');

    return <ItemList>{items.map(item => this.renderShelfListItem(item))}</ItemList>;
  }

  renderBatchActionsList() {
    const { shelf, shelfTransactions } = this.props;
    const items = shelf.get('items');
    const pageNumber = shelf.getIn(['pagination', 'page']);
    const pageLimit = shelf.getIn(['pagination', 'limit']);
    const totalCount = shelf.getIn(['pagination', 'count']);
    const shelfName = shelf.get('name');

    const actionIsLoading = shelfTransactions
      .toList()
      .some(item => item.get('isBatchRemoving') || item.get('isBatchMoving') || item.get('isBatchUpdatingPrivacy'));

    return (
      <BatchActionsListContainer
        list={items}
        actionIsLoading={actionIsLoading}
        pageNumber={pageNumber}
        pageLimit={pageLimit}
        totalItems={totalCount}
        renderActionsMenu={this.renderBatchActionsMenu}
      >
        {(selectedActionItems, updateItem) => (
          <div>
            <ItemList>
              {items.map((item, index) =>
                this.renderBatchActionsListItem({
                  item,
                  selectedActionItems,
                  updateItem,
                  index
                })
              )}
            </ItemList>
            <DeleteConfirmationModal
              modalKey={BATCH_REMOVE_MODAL_KEY}
              items={selectedActionItems.toList()}
              shelfActions={this.props.shelfActions}
              modalActions={this.props.modalActions}
              postSuccess={() => this.redirectToShelfPage(shelfName, selectedActionItems)}
              isLoading={actionIsLoading}
              isBatch
            />
          </div>
        )}
      </BatchActionsListContainer>
    );
  }

  renderShelfListItem(item) {
    const {
      availabilityDetailActions,
      commentsActions,
      quotationsActions,
      tagsActions,
      shelfActions,
      tagsTransactions,
      videosActions,
      iOwnThisActions,
      similarTitlesActions,
      shelfTransactions,
      privateNotesActions,
      contentAdvisoriesActions,
      ageSuitabilitiesActions,
      summariesActions,
      bibs,
      shelf,
      owner,
      ownerComments,
      ownerQuotations,
      ownerRatings,
      ownerTags,
      ownerVideos,
      ownerSimilarTitles,
      ownerPrivateNotes,
      ownerContentAdvisories,
      ownerAgeSuitabilities,
      ownerSummaries,
      iOwnThisSettings,
      currentLibrary,
      commentsTransactions,
      quotationsTransactions,
      videosTransactions,
      iOwnThisTransactions,
      similarTitlesTransactions,
      privateNotesTransactions,
      contentAdvisoriesTransactions,
      ageSuitabilitiesTransactions,
      summariesTransactions,
      currentUserShelves,
      modalActions,
      handleAvailabilityLinkClick,
      trackContentCollectionMove
    } = this.props;

    const metadataId = item.get('metadataId');

    return (
      <ShelfListItem
        key={`shelf-list-item-${item.get('id')}`}
        item={item}
        owner={owner}
        shelfName={shelf.get('name')}
        bib={bibs.get(metadataId)}
        currentLibrary={currentLibrary}
        currentUserShelfItemForBib={currentUserShelves.get(metadataId)}
        ownerCommentForBib={ownerComments.get(metadataId)}
        ownerQuotationsForBib={ownerQuotations.get(metadataId)}
        ownerRatingForBib={ownerRatings.get(metadataId)}
        ownerTagsForBib={ownerTags.get(metadataId)}
        ownerVideoForBib={ownerVideos.get(metadataId)}
        ownerSettingForBib={iOwnThisSettings.get(metadataId)}
        ownerSimilarTitlesForBib={ownerSimilarTitles.get(metadataId)}
        ownerPrivateNoteForBib={ownerPrivateNotes.get(metadataId)}
        ownerContentAdvisoriesForBib={ownerContentAdvisories.get(metadataId)}
        ownerAgeSuitabilityForBib={ownerAgeSuitabilities.get(metadataId)}
        ownerSummaryForBib={ownerSummaries.get(metadataId)}
        similarTitleBibs={this.getBibsForSimilarTitles(ownerSimilarTitles, metadataId)}
        commentsTransactions={commentsTransactions}
        quotationsTransactions={quotationsTransactions}
        tagsTransactions={tagsTransactions}
        videosTransactions={videosTransactions}
        iOwnThisTransactions={iOwnThisTransactions}
        similarTitlesTransactions={similarTitlesTransactions}
        privateNotesTransactions={privateNotesTransactions}
        contentAdvisoriesTransactions={contentAdvisoriesTransactions}
        ageSuitabilitiesTransactions={ageSuitabilitiesTransactions}
        summariesTransactions={summariesTransactions}
        shelfTransactions={shelfTransactions}
        availabilityDetailActions={availabilityDetailActions}
        commentsActions={commentsActions}
        quotationsActions={quotationsActions}
        tagsActions={tagsActions}
        videosActions={videosActions}
        similarTitlesActions={similarTitlesActions}
        iOwnThisActions={iOwnThisActions}
        privateNotesActions={privateNotesActions}
        contentAdvisoriesActions={contentAdvisoriesActions}
        ageSuitabilitiesActions={ageSuitabilitiesActions}
        summariesActions={summariesActions}
        shelfActions={shelfActions}
        modalActions={modalActions}
        handleAvailabilityLinkClick={handleAvailabilityLinkClick}
        trackContentCollectionMove={trackContentCollectionMove}
      />
    );
  }

  renderBatchActionsListItem({ item, selectedActionItems, updateItem, index }) {
    return (
      <BatchActionsListItem
        key={`batch-actions-list-item-${item.get('id')}`}
        index={index}
        title={item.get('title')}
        actionItem={item}
        selectedItems={selectedActionItems}
        updateItem={updateItem}
        dataKey="shelf-list-item"
      >
        {this.renderShelfListItem(item)}
      </BatchActionsListItem>
    );
  }

  renderBatchActionsMenu(selectedActionItems) {
    const { shelf } = this.props;
    const selectedCount = selectedActionItems.size;

    const moveMenuHeader = (
      <DropdownMenuHeader key="shelf-batch-actions-header">
        <FormattedHTMLMessage id="move_to_shelf" /> ({selectedCount})
      </DropdownMenuHeader>
    );

    const shelfNamesForMove = Immutable.Map(SHELF_NAMES)
      .valueSeq()
      .toArray()
      .filter(shelfName => shelfName !== shelf.get('name'));
    const moveMenuItems = shelfNamesForMove.map(shelfName => (
      <DropdownMenuItem key={`shelf-batch-actions-menu-item-move-${shelfName}`}>
        <BatchActionLink
          href="#shelf-batch-move"
          className="shelf-batch-move"
          handleClick={e => this.handleBatchMove(e, shelfName, selectedActionItems)}
          dataKey="move-shelf-item-batch-action-link"
        >
          <FormattedMessage id={`label_shelf_${shelfName}`} />
        </BatchActionLink>
      </DropdownMenuItem>
    ));

    const menuDivider = <DropdownMenuDivider key="shelf-batch-actions-divider" />;
    const menuDivider2 = <DropdownMenuDivider key="shelf-batch-actions-divider2" />;

    const removeMenuItem = (
      <DropdownMenuItem key="shelf-batch-actions-menu-item-remove">
        <BatchActionLink
          href="#shelf-batch-remove"
          className="shelf-batch-remove"
          handleClick={this.handleBatchRemoveModalOpen}
          dataKey="remove-shelf-item-batch-action-link"
        >
          <FormattedMessage id="button_remove_from_shelves" /> ({selectedCount})
        </BatchActionLink>
      </DropdownMenuItem>
    );

    const eligibleToMarkPrivate = selectedActionItems.filter(item => !item.get('privateItem'));
    const markPrivate = (
      <DropdownMenuItem key="shelf-batch-actions-menu-mark-private">
        <BatchActionLink
          href="#shelf-batch-private"
          className="shelf-batch-link-private"
          handleClick={e => this.handlePrivacyToggle(e, eligibleToMarkPrivate, true, 'marked_as_private')}
          disabled={privacyStatusCheck(eligibleToMarkPrivate, true)}
          dataKey="mark-private-batch-action-link"
        >
          <FormattedMessage id="mark_as_private" /> ({eligibleToMarkPrivate.size})
        </BatchActionLink>
      </DropdownMenuItem>
    );

    const eligibleToMarkShared = selectedActionItems.filter(item => item.get('privateItem'));
    const markShared = (
      <DropdownMenuItem
        key="shelf-batch-actions-menu-mark-shared"
        disabled={privacyStatusCheck(selectedActionItems, false)}
      >
        <BatchActionLink
          href="#shelf-batch-shared"
          className="shelf-batch-link-shared"
          handleClick={e => this.handlePrivacyToggle(e, eligibleToMarkShared, false, 'marked_as_shared')}
          disabled={privacyStatusCheck(eligibleToMarkShared, false)}
          dataKey="mark-shared-batch-action-link"
        >
          <FormattedMessage id="mark_as_shared" /> ({eligibleToMarkShared.size})
        </BatchActionLink>
      </DropdownMenuItem>
    );

    return [moveMenuHeader, ...moveMenuItems, menuDivider, markPrivate, markShared, menuDivider2, removeMenuItem];
  }

  render() {
    const { owner } = this.props;

    return (
      <div className="cp-shelf-list">
        {owner.get('loggedIn') ? this.renderBatchActionsList() : this.renderBibList()}
      </div>
    );
  }
}

ShelfList.propTypes = {
  currentLibrary: ImmutablePropTypes.map.isRequired,
  owner: userIdentityShape.isRequired,
  shelf: shelfShape.isRequired,
  bibs: ImmutablePropTypes.map.isRequired,
  commentsTransactions: ImmutablePropTypes.map.isRequired,
  quotationsTransactions: ImmutablePropTypes.map.isRequired,
  tagsTransactions: ImmutablePropTypes.map.isRequired,
  videosTransactions: ImmutablePropTypes.map.isRequired,
  iOwnThisTransactions: ImmutablePropTypes.map.isRequired,
  shelfTransactions: ImmutablePropTypes.map.isRequired,
  similarTitlesTransactions: ImmutablePropTypes.map.isRequired,
  privateNotesTransactions: ImmutablePropTypes.map.isRequired,
  contentAdvisoriesTransactions: ImmutablePropTypes.map.isRequired,
  ageSuitabilitiesTransactions: ImmutablePropTypes.map.isRequired,
  summariesTransactions: ImmutablePropTypes.map.isRequired,
  ownerComments: ImmutablePropTypes.map.isRequired,
  ownerQuotations: ImmutablePropTypes.map.isRequired,
  ownerRatings: ImmutablePropTypes.map.isRequired,
  ownerTags: ImmutablePropTypes.map.isRequired,
  ownerVideos: ImmutablePropTypes.map.isRequired,
  ownerSimilarTitles: ImmutablePropTypes.map.isRequired,
  ownerPrivateNotes: ImmutablePropTypes.map.isRequired,
  ownerContentAdvisories: ImmutablePropTypes.map.isRequired,
  ownerAgeSuitabilities: ImmutablePropTypes.map.isRequired,
  ownerSummaries: ImmutablePropTypes.map.isRequired,
  iOwnThisSettings: ImmutablePropTypes.map.isRequired,
  availabilityDetailActions: PropTypes.objectOf(PropTypes.func).isRequired,
  commentsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  quotationsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  tagsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  videosActions: PropTypes.objectOf(PropTypes.func).isRequired,
  iOwnThisActions: PropTypes.objectOf(PropTypes.func).isRequired,
  similarTitlesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  privateNotesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  contentAdvisoriesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  ageSuitabilitiesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  summariesActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  currentUserShelves: ImmutablePropTypes.map.isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func),
  router: routerShape.isRequired,
  handleAvailabilityLinkClick: PropTypes.func,
  trackContentCollectionMove: PropTypes.func
};

export default withRouter(ShelfList);
